import React, { useRef, useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// Optimized Image component with lazy loading and blur placeholder
const OptimizedImage = ({ src, alt, className, onClick, priority = false }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef(null);

  useEffect(() => {
    if (priority) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Start loading 200px before visible
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  return (
    <div ref={imgRef} className={`relative ${className}`} onClick={onClick}>
      {/* Blur placeholder */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br from-stone-200 to-stone-300 transition-opacity duration-500 ${isLoaded ? 'opacity-0' : 'opacity-100'}`}
      >
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
      </div>
      
      {isInView && (
        <img
          src={src}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setIsLoaded(true)}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
};

const ParallaxGallery = ({ images }) => {
  const containerRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxLoaded, setLightboxLoaded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        if (rect.top < windowHeight && rect.bottom > 0) {
          setIsInView(true);
          const progress = (windowHeight - rect.top) / (windowHeight + rect.height);
          setScrollY(progress);
        } else {
          setIsInView(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openGallery = useCallback((index) => {
    setCurrentIndex(index);
    setIsOpen(true);
    setLightboxLoaded(false);
  }, []);

  const closeGallery = useCallback(() => {
    setIsOpen(false);
  }, []);

  const goToPrevious = useCallback(() => {
    setLightboxLoaded(false);
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setLightboxLoaded(false);
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  // Handle body overflow when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') closeGallery();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeGallery, goToPrevious, goToNext]);

  // Define parallax layers
  const layers = [
    { indices: [0, 4, 8], speed: 0.3, scale: 1.1, zIndex: 1 },
    { indices: [1, 5, 9], speed: 0.5, scale: 1.0, zIndex: 2 },
    { indices: [2, 6], speed: 0.7, scale: 0.95, zIndex: 3 },
    { indices: [3, 7], speed: 0.9, scale: 0.9, zIndex: 4 },
  ];

  const getImageStyle = (layerIndex, positionInLayer) => {
    const layer = layers[layerIndex];
    const baseOffset = (scrollY - 0.5) * 100 * layer.speed;
    
    const positions = [
      { x: -5, y: -10 },
      { x: 5, y: 5 },
      { x: -3, y: 8 },
      { x: 7, y: -5 },
    ];
    
    const pos = positions[positionInLayer % positions.length];
    
    return {
      transform: `
        translateY(${baseOffset + pos.y}px) 
        translateX(${pos.x}px)
        scale(${layer.scale})
        perspective(1000px)
        rotateX(${(scrollY - 0.5) * 3}deg)
      `,
      zIndex: layer.zIndex,
      transition: 'transform 0.1s ease-out',
    };
  };

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 1.5s infinite;
        }
      `}</style>
      
      <div 
        ref={containerRef}
        className="relative py-16 overflow-hidden"
        data-testid="parallax-gallery"
      >
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-amber-800/80 text-sm tracking-[0.2em] uppercase mb-3">
            Photo Gallery
          </p>
          <h3 className="text-2xl md:text-3xl font-light text-stone-800 mb-4">
            空間デザインギャラリー
          </h3>
          <div className="w-16 h-px bg-amber-700/40 mx-auto" />
        </div>

        {/* 3D Perspective Gallery Grid */}
        <div className="relative max-w-6xl mx-auto px-4">
          {/* Gradient overlays for depth effect */}
          <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          
          {/* Main gallery container with perspective */}
          <div 
            className="relative"
            style={{ 
              perspective: '1200px',
              perspectiveOrigin: '50% 50%',
            }}
          >
            {/* Grid with staggered 3D effect */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
              {images.map((image, index) => {
                let layerIndex = 0;
                let positionInLayer = 0;
                for (let i = 0; i < layers.length; i++) {
                  const idx = layers[i].indices.indexOf(index);
                  if (idx !== -1) {
                    layerIndex = i;
                    positionInLayer = idx;
                    break;
                  }
                }

                const isLargeImage = index === 0 || index === 5;
                const isWideImage = index === 2;

                return (
                  <button
                    key={index}
                    onClick={() => openGallery(index)}
                    className={`
                      relative overflow-hidden rounded-xl group cursor-pointer
                      ${isLargeImage ? 'col-span-2 row-span-2' : ''}
                      ${isWideImage ? 'md:col-span-2' : ''}
                    `}
                    style={isInView ? getImageStyle(layerIndex, positionInLayer) : {}}
                    data-testid={`gallery-image-${index}`}
                  >
                    <OptimizedImage
                      src={image.url}
                      alt={image.alt}
                      priority={index < 4} // Load first 4 images immediately
                      className={`w-full overflow-hidden ${isLargeImage ? 'aspect-square' : 'aspect-[4/3]'}`}
                    />
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 
                      opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    
                    {/* View indicator */}
                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-center
                      opacity-0 group-hover:opacity-100 transition-all duration-500 transform
                      translate-y-4 group-hover:translate-y-0 pointer-events-none">
                      <span className="text-white text-sm font-medium tracking-wider bg-black/40 
                        backdrop-blur-sm px-4 py-2 rounded-full">
                        詳細を見る
                      </span>
                    </div>
                    
                    {/* Corner accent */}
                    <div className="absolute top-0 right-0 w-16 h-16 opacity-0 group-hover:opacity-100
                      transition-opacity duration-500 pointer-events-none">
                      <div className="absolute top-2 right-2 w-8 h-8 border-t-2 border-r-2 border-white/60 rounded-tr-lg" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Floating decoration elements */}
          <div 
            className="absolute -top-8 -left-8 w-32 h-32 bg-amber-200/20 rounded-full blur-3xl pointer-events-none"
            style={{ 
              transform: `translateY(${(scrollY - 0.5) * 50}px)`,
              transition: 'transform 0.2s ease-out'
            }}
          />
          <div 
            className="absolute -bottom-8 -right-8 w-40 h-40 bg-amber-300/20 rounded-full blur-3xl pointer-events-none"
            style={{ 
              transform: `translateY(${(scrollY - 0.5) * -30}px)`,
              transition: 'transform 0.2s ease-out'
            }}
          />
        </div>

        {/* Caption */}
        <p className="text-center text-stone-500 text-sm mt-8 font-light">
          クリックして拡大表示 • ACOEの空間デザインをご覧ください
        </p>
      </div>

      {/* Lightbox Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center animate-in fade-in duration-300"
          onClick={closeGallery}
          data-testid="gallery-lightbox"
        >
          {/* Close Button */}
          <button
            onClick={closeGallery}
            className="absolute top-6 right-6 z-[10000] p-3 rounded-full bg-white/10 hover:bg-white/20 
              text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
            data-testid="gallery-close-btn"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-[10000] p-3 rounded-full 
              bg-white/10 hover:bg-white/20 text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
            data-testid="gallery-prev-btn"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          {/* Next Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-[10000] p-3 rounded-full 
              bg-white/10 hover:bg-white/20 text-white transition-all duration-200 hover:scale-110 backdrop-blur-sm"
            data-testid="gallery-next-btn"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Image Container */}
          <div
            className="relative w-full h-full flex items-center justify-center p-16 md:p-20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Loading spinner */}
            {!lightboxLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
              </div>
            )}
            <img
              key={currentIndex}
              src={images[currentIndex].url}
              alt={images[currentIndex].alt}
              className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${lightboxLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setLightboxLoaded(true)}
            />
          </div>

          {/* Image Counter */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white text-sm 
            bg-black/50 backdrop-blur-sm px-6 py-2 rounded-full z-[10000]">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Thumbnail Strip */}
          <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex gap-2 z-[10000] 
            max-w-[90vw] overflow-x-auto pb-2 px-4">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxLoaded(false);
                  setCurrentIndex(index);
                }}
                className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden transition-all duration-200 
                  ${index === currentIndex 
                    ? 'ring-2 ring-white scale-110' 
                    : 'opacity-50 hover:opacity-100'
                  }`}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ParallaxGallery;
